/**
 * SDL Store Screen
 *
 * 3-tab store: Stacks (IAP packs + surprise chest hero), Offers (empty for now),
 * Free Stacks (placeholder). Matches mockups/store.html and v1 economy spec.
 *
 * Tap handlers emit events; actual IAP / chest claim flows wire to those.
 *
 * Spec reference: docs/economy/v1-spec.md §4-8.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * STATUS: First-pass UI complete. Backend wiring is STUBBED.
 *
 * TODO (wire-up checklist, prioritized):
 *
 * 1. Wallet state — DEFAULT_DATA hardcodes 2.4B / 3,450 / 14. Replace with
 *    real player balance via `shopScreen.updateWallet({ ... })` on:
 *      - `screen:change` to=shop (fresh values on entry)
 *      - after successful purchase
 *      - after chest claim
 *      - on `wallet:update` server event
 *
 * 2. Bundle data — DEFAULT_DATA.bundles is hardcoded. Should come from
 *    a server endpoint (e.g. GET /api/store/bundles) so contents reflect
 *    peak-balance scaling (`getBoostedStacks` in scripts/economy/iap.ts)
 *    and LiveOps can flip the `isBest` flag without a client redeploy.
 *
 * 3. IAP purchase flow — `purchase` event emits {productId}. Needs:
 *      - Capacitor in-app-purchase plugin install + product registration
 *      - Server validation (Apple receipt / Google purchase token).
 *        Reference: ~/projects/PhoneDice/dice-server/server.js lines 800+
 *      - On success: server credits wallet + responds; UI shows confetti
 *        + animated wallet count-up
 *      - On failure / cancel: toast bottom-center
 *      - First-purchase 2× Gold bonus tracked per SKU per player (spec §5d)
 *
 * 4. Surprise chest claim — `claim-chest` event needs:
 *      - Server endpoint that runs generateChest(peakStacks) from chest.ts
 *      - Per-player weekly counter (`surpriseChest.remainingThisWeek` 0-4)
 *        backed by Mongo or Redis with a weekly reset
 *      - Reward animation modal: chest-open with rolled amount, green
 *        particle effects, count-up on the Stacks pill
 *      - When counter hits 0: show "Resets in Xd Xh" + disable CTA
 *
 * 5. Analytics — every IAP transaction must log `offer_id` (or
 *    `static_store`), `surface`, and `player_state_snapshot`
 *    (current_balance, peak_balance, days_since_last_play, lifetime_spend).
 *    See spec §8e. Without this we can't decompose offer vs. static
 *    performance — exactly the gap Phone Dice has.
 *
 * 6. Offers tab — currently empty state. Replace with offer cards driven
 *    by the offer engine (spec §8). The OfferTemplate schema is already
 *    designed; needs server implementation + admin UI.
 *
 * 7. Free Stacks tab — currently "coming soon" placeholder. Phase 2 work:
 *    watch-ad rewards, daily login chain, daily challenges, achievement
 *    milestones. See spec §6c + §13.
 *
 * 8. Formatting — currently pass pre-formatted strings ("2.4B"). Standardize
 *    a client-side `formatStacks(bigint)` helper that matches the K/M/B/T
 *    abbreviation in scripts/economy/format.ts (server side).
 *
 * 9. Visual polish (optional, low priority):
 *      - Re-add 3 chest sparkles (CSS was removed; trivial to re-add)
 *      - Real chest illustration (current SVG is placeholder)
 *      - Real lucky charm icon for the bonus line
 *      - Haptic feedback on iOS via Capacitor Haptics plugin
 *
 * Files touched by this implementation:
 *   - client/src/ui/screens/StoreScreen.ts (this file)
 *   - client/src/ui/styles/store.css
 *   - client/src/ui/styles/main.css (added @import './store.css')
 *   - client/src/ui/screens/index.ts (exports)
 *   - client/src/ui/index.ts (re-exports)
 *   - client/src/ui/UIManager.ts (registerScreen now preserves classes)
 *   - client/src/main.ts (registers shopScreen, wires event stubs, reset scroll on exit)
 * ─────────────────────────────────────────────────────────────────────────
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import type { OfferWire } from '@sdl/shared';

export type StoreTab = 'stacks' | 'offers' | 'free';

export interface StoreWallet {
  stacks: string;       // pre-formatted, e.g. "2.4B"
  gold: string;         // pre-formatted, e.g. "3,450"
  diceTokens: string;   // pre-formatted, e.g. "14"
}

export interface StoreSurpriseChest {
  remainingThisWeek: number;
  rewardRangeLabel: string; // e.g. "25M – 50M"
}

export interface StoreBundleDef {
  productId: string;
  name: string;
  usd: string;
  stacks: string;       // pre-formatted
  gold: string;
  diceTokens: string;
  accentClass: 'starter' | 'hood' | 'corner' | 'block' | 'kingpin' | 'legacy';
  bonusLine?: string;   // optional, e.g. "1× Lucky Charm"
  isBest?: boolean;     // BEST DEAL treatment
}

export interface StoreScreenData {
  wallet: StoreWallet;
  surpriseChest: StoreSurpriseChest;
  bundles: StoreBundleDef[];
}

const DEFAULT_DATA: StoreScreenData = {
  wallet: { stacks: '2.4B', gold: '3,450', diceTokens: '14' },
  surpriseChest: { remainingThisWeek: 3, rewardRangeLabel: '25M – 50M' },
  bundles: [
    { productId: 'sdl_pack_099',  name: 'STARTER ROLL',  usd: '$0.99',  stacks: '25M',   gold: '100',    diceTokens: '1',   accentClass: 'starter' },
    { productId: 'sdl_pack_299',  name: 'HOOD RUN',      usd: '$2.99',  stacks: '100M',  gold: '400',    diceTokens: '3',   accentClass: 'hood'    },
    { productId: 'sdl_pack_499',  name: 'CORNER HUSTLE', usd: '$4.99',  stacks: '500M',  gold: '1,000',  diceTokens: '6',   accentClass: 'corner'  },
    { productId: 'sdl_pack_999',  name: 'BLOCK BOSS',    usd: '$9.99',  stacks: '2.5B',  gold: '2,500',  diceTokens: '13',  accentClass: 'block'   },
    { productId: 'sdl_pack_1999', name: 'CITY MONEY',    usd: '$19.99', stacks: '75B',   gold: '25,000',  diceTokens: '35',  accentClass: 'block',   isBest: true },
    { productId: 'sdl_pack_4999', name: 'KINGPIN CUT',   usd: '$49.99', stacks: '200B',  gold: '65,000',  diceTokens: '62',  accentClass: 'kingpin' },
    { productId: 'sdl_pack_9999', name: 'LEGACY DROP',   usd: '$99.99', stacks: '500B',  gold: '150,000', diceTokens: '101', accentClass: 'legacy'  },
  ],
};

/** Surprise chest is not part of the first release. Flip to true to re-enable. */
const SHOW_SURPRISE_CHEST = false;

export class StoreScreen extends EventEmitter {
  public element: HTMLElement;
  private data: StoreScreenData;
  private currentTab: StoreTab = 'stacks';
  /** Live offers (set by main.ts from OfferService). */
  private offers: OfferWire[] = [];

  constructor(data?: Partial<StoreScreenData>) {
    super();
    this.data = { ...DEFAULT_DATA, ...data };
    this.element = this.createElement();
    this.attachListeners();
  }

  private createElement(): HTMLElement {
    const screen = document.createElement('div');
    screen.classList.add('store-screen');
    screen.innerHTML = `
      <div class="store-topbar">
        <div class="store-title-block">
          <div class="store-title">STORE</div>
          <div class="store-title-sub">Stack · Spend · Stunt</div>
        </div>
      </div>

      ${this.renderWallet()}

      <div class="store-tabs">
        <button class="store-tab active" data-tab="stacks">STACKS</button>
        <button class="store-tab" data-tab="offers">OFFERS</button>
        <button class="store-tab" data-tab="free">FREE STACKS</button>
      </div>
      <div class="store-tabs-divider"></div>

      <div class="store-content">
        ${this.renderStacksPanel()}
        ${this.renderOffersPanel()}
        ${this.renderFreePanel()}
      </div>
    `;
    return screen;
  }

  private renderWallet(): string {
    const { wallet } = this.data;
    return `
      <div class="store-wallet">
        <div class="store-pill-col">
          <div class="store-pill-title">Stacks</div>
          <div class="store-pill stacks">
            <div class="store-pill-ico">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#062" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="7" width="18" height="11" rx="1.5"/>
                <circle cx="12" cy="12.5" r="2.6"/>
              </svg>
            </div>
            <div class="store-pill-val">${wallet.stacks}</div>
          </div>
        </div>
        <div class="store-pill-col">
          <div class="store-pill-title">Gold</div>
          <div class="store-pill gold">
            <div class="store-pill-ico">
              <img src="/assets/icons/currency/gold.svg" alt="Gold">
            </div>
            <div class="store-pill-val">${wallet.gold}</div>
          </div>
        </div>
        <div class="store-pill-col">
          <div class="store-pill-title">Dice Tokens</div>
          <div class="store-pill tokens">
            <div class="store-pill-ico">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="rgba(255,255,255,0.95)"/>
                <circle cx="8.5" cy="8.5" r="1.6" fill="#a00050"/>
                <circle cx="15.5" cy="8.5" r="1.6" fill="#a00050"/>
                <circle cx="8.5" cy="15.5" r="1.6" fill="#a00050"/>
                <circle cx="15.5" cy="15.5" r="1.6" fill="#a00050"/>
                <circle cx="12" cy="12" r="1.6" fill="#a00050"/>
              </svg>
            </div>
            <div class="store-pill-val">${wallet.diceTokens}</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderStacksPanel(): string {
    return `
      <div class="store-panel active" data-panel="stacks">
        ${SHOW_SURPRISE_CHEST ? this.renderSurpriseChest() : ''}
        <div class="store-section-h">
          <h2>STACK PACKS</h2>
          <span class="hint">USD · Tap to buy</span>
        </div>
        ${this.data.bundles.map((b) => this.renderBundle(b)).join('')}
        <div class="store-footer-note">all sales final · prices in USD</div>
      </div>
    `;
  }

  private renderSurpriseChest(): string {
    const { remainingThisWeek, rewardRangeLabel } = this.data.surpriseChest;
    return `
      <div class="store-hero" data-action="claim-chest" role="button">
        <div class="store-hero-row">
          <div class="store-chest">
            <svg viewBox="0 0 80 80">
              <defs>
                <linearGradient id="storeChestBody" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stop-color="#FFE684"/>
                  <stop offset="0.55" stop-color="#FFB300"/>
                  <stop offset="1" stop-color="#7A4D00"/>
                </linearGradient>
                <linearGradient id="storeChestLid" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stop-color="#FFF1AE"/>
                  <stop offset="1" stop-color="#E59E00"/>
                </linearGradient>
                <radialGradient id="storeChestGlow" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0" stop-color="rgba(255,255,255,0.95)"/>
                  <stop offset="1" stop-color="rgba(255,255,255,0)"/>
                </radialGradient>
              </defs>
              <ellipse cx="40" cy="34" rx="32" ry="14" fill="url(#storeChestGlow)" opacity="0.55"/>
              <rect x="14" y="36" width="52" height="32" rx="4" fill="url(#storeChestBody)" stroke="#3a2200" stroke-width="1.3"/>
              <path d="M14 36 Q14 22 40 22 Q66 22 66 36 Z" fill="url(#storeChestLid)" stroke="#3a2200" stroke-width="1.3"/>
              <rect x="14" y="46" width="52" height="3.5" fill="#3a2200" opacity=".55"/>
              <rect x="37" y="22" width="6" height="46" fill="#3a2200" opacity=".55"/>
              <rect x="35" y="42" width="10" height="11" rx="2" fill="#FFEFAE" stroke="#3a2200" stroke-width="1"/>
              <circle cx="40" cy="47" r="1.8" fill="#3a2200"/>
              <path d="M18 30 Q26 25 40 25" stroke="rgba(255,255,255,.7)" stroke-width="1.5" fill="none" stroke-linecap="round"/>
              <circle cx="26" cy="38" r="3" fill="#FFE684" stroke="#7a5300" stroke-width=".8"/>
              <circle cx="54" cy="38" r="3" fill="#FFE684" stroke="#7a5300" stroke-width=".8"/>
              <path d="M30 22 Q34 16 40 22 Q46 16 50 22" stroke="#FF0080" stroke-width="2.5" fill="none" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="store-hero-text">
            <span class="store-hero-eyebrow"><span class="dotg"></span>Weekly Drop</span>
            <div class="store-hero-title">SURPRISE CHEST</div>
            <div class="store-hero-sub">Tap to claim — ${remainingThisWeek} left this week</div>
          </div>
        </div>
        <div class="store-hero-foot">
          <div class="store-reward-range">
            <div class="rr-ico"></div>
            <div>
              <div class="rr-val">${rewardRangeLabel}</div>
              <div class="rr-label">Stacks</div>
            </div>
          </div>
          <button class="store-claim-btn" data-action="claim-chest">CLAIM</button>
        </div>
      </div>
    `;
  }

  private renderBundle(b: StoreBundleDef): string {
    const classes = ['store-bundle'];
    if (b.isBest) {
      classes.push('best');
    } else {
      classes.push(`t-${b.accentClass}`);
    }
    const badge = b.isBest ? `<span class="store-best-badge">BEST DEAL</span>` : '';
    const bonus = b.bonusLine ? `<div class="store-bonus-line">${b.bonusLine}</div>` : '';
    return `
      <div class="${classes.join(' ')}" data-action="buy" data-product="${b.productId}" data-sound="button">
        ${badge}
        <div class="store-bundle-left">
          <div class="store-bundle-name">${b.name}</div>
          <div class="store-bundle-contents">
            <span class="store-chip stk"><span class="ci"></span><span>${b.stacks}</span></span>
            <span class="store-chip gld"><span class="ci"></span><span>${b.gold}</span></span>
            <span class="store-chip tok"><span class="ci"></span><span>${b.diceTokens}</span></span>
          </div>
          ${bonus}
        </div>
        <button class="store-price-btn"><span class="usd">USD</span>${b.usd}</button>
      </div>
    `;
  }

  private renderOffersPanel(): string {
    return `
      <div class="store-panel" data-panel="offers">
        <div class="store-offers-grid">${this.renderOfferCards()}</div>
      </div>
    `;
  }

  /** Inner HTML for the offers grid — one tappable baked card per offer, or the
   *  empty state when there are none live. Rebuilt by setOffers(). */
  private renderOfferCards(): string {
    if (this.offers.length === 0) {
      return `
        <div class="store-empty">
          <div class="store-empty-icon">
            <svg viewBox="0 0 48 48" fill="none">
              <defs>
                <linearGradient id="storeMb1" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stop-color="#00F5FF" stop-opacity=".9"/>
                  <stop offset="1" stop-color="#0088AA" stop-opacity=".7"/>
                </linearGradient>
              </defs>
              <rect x="22" y="30" width="3" height="14" fill="rgba(255,255,255,.3)"/>
              <path d="M8 18 Q8 12 14 12 H34 Q40 12 40 18 V32 H8 Z" stroke="url(#storeMb1)" stroke-width="2" fill="rgba(0,245,255,0.05)"/>
              <rect x="11" y="20" width="10" height="9" rx="1" stroke="rgba(255,255,255,.5)" stroke-width="1.4" fill="none"/>
              <circle cx="19" cy="24.5" r="0.9" fill="rgba(255,255,255,.6)"/>
              <rect x="38" y="14" width="6" height="5" fill="#FF3366"/>
              <path d="M40 14 V32" stroke="rgba(255,255,255,.5)" stroke-width="1.2"/>
            </svg>
          </div>
          <h3>NO ACTIVE OFFERS</h3>
          <p>New offers drop regularly — check back later for limited deals and bonuses.</p>
          <div class="pulse-dot">Watching for drops</div>
        </div>
      `;
    }
    // No loading="lazy": these posters ARE the visible content of the tab, and
    // OfferService has already prefetched them, so they should paint at once.
    // The card carries a neon skeleton shimmer until `is-loaded` is set; the img
    // fades in on load (see store.css). Warm-cache images are marked loaded
    // synchronously in wireOfferImages() so no shimmer flashes.
    return this.offers
      .map(
        (o) => `
        <button class="store-offer-card" data-offer-id="${o.id}">
          <img src="${o.img}" alt="Offer" decoding="async" />
        </button>`,
      )
      .join('');
  }

  /** Set the live offers and re-render the OFFERS tab grid. */
  setOffers(offers: OfferWire[]): void {
    this.offers = offers;
    const grid = this.element.querySelector<HTMLElement>('.store-offers-grid');
    if (grid) {
      grid.innerHTML = this.renderOfferCards();
      this.wireOfferImages(grid);
    }
  }

  /**
   * Mark each offer card `is-loaded` once its poster is ready, so the skeleton
   * shimmer hides and the image fades in. Cached posters (the common case, since
   * OfferService prefetches on auth) report `complete` immediately and are marked
   * synchronously — no shimmer flash. A failed load is also marked loaded so a
   * broken poster doesn't shimmer forever.
   */
  private wireOfferImages(grid: HTMLElement): void {
    grid.querySelectorAll<HTMLImageElement>('.store-offer-card img').forEach((img) => {
      const card = img.closest('.store-offer-card');
      if (!card) return;
      const markLoaded = () => card.classList.add('is-loaded');
      if (img.complete && img.naturalWidth > 0) {
        markLoaded();
      } else {
        img.addEventListener('load', markLoaded, { once: true });
        img.addEventListener('error', markLoaded, { once: true });
      }
    });
  }

  private renderFreePanel(): string {
    return `
      <div class="store-panel empty-panel" data-panel="free">
        <div class="store-empty coming">
          <div class="store-empty-icon">
            <svg viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="18" stroke="rgba(255,0,128,.7)" stroke-width="2" fill="rgba(255,0,128,0.05)"/>
              <circle cx="24" cy="24" r="12" stroke="rgba(255,255,255,.35)" stroke-width="1.4"/>
              <circle cx="24" cy="24" r="6" stroke="rgba(255,215,0,.7)" stroke-width="1.4"/>
              <circle cx="24" cy="24" r="2" fill="#FFD700"/>
              <path d="M40 8 L24 24" stroke="#00FF88" stroke-width="2" stroke-linecap="round"/>
              <path d="M40 8 L36 8 M40 8 L40 12" stroke="#00FF88" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <h3 style="color:#FF66A8;">COMING SOON</h3>
          <p>Free Stacks rewards — watch ads, complete challenges, claim daily bonuses. Launching soon.</p>
          <div class="pulse-dot">In Production</div>
        </div>
      </div>
    `;
  }

  private attachListeners(): void {
    // Tab switching
    this.element.querySelectorAll<HTMLElement>('.store-tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.dataset.tab as StoreTab | undefined;
        if (target) this.switchTab(target);
      });
    });

    // Bundle purchase
    this.element.querySelectorAll<HTMLElement>('.store-bundle').forEach((bundle) => {
      bundle.addEventListener('click', (e) => {
        const productId = bundle.dataset.product;
        if (productId) {
          this.emit('purchase', { productId });
        }
        e.stopPropagation();
      });
    });

    // Surprise chest claim
    const chestHero = this.element.querySelector<HTMLElement>('.store-hero');
    if (chestHero) {
      chestHero.addEventListener('click', () => {
        this.emit('claim-chest', undefined);
      });
    }

    // Offer card taps (delegated — the grid is rebuilt by setOffers()).
    const offersPanel = this.element.querySelector<HTMLElement>('[data-panel="offers"]');
    offersPanel?.addEventListener('click', (e) => {
      const card = (e.target as HTMLElement).closest<HTMLElement>('.store-offer-card');
      const offerId = card?.dataset.offerId;
      if (!offerId) return;
      const offer = this.offers.find((o) => o.id === offerId);
      if (offer) this.emit('offer:tap', { offer });
    });
  }

  private switchTab(target: StoreTab): void {
    if (target === this.currentTab) return;
    this.currentTab = target;

    this.element.querySelectorAll<HTMLElement>('.store-tab').forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.tab === target);
    });

    this.element.querySelectorAll<HTMLElement>('.store-panel').forEach((panel) => {
      panel.classList.toggle('active', panel.dataset.panel === target);
    });

    const content = this.element.querySelector<HTMLElement>('.store-content');
    if (content) content.scrollTop = 0;

    this.emit('tab:change', { tab: target });
  }

  /** Reset the Stack Packs scroll to top. Call when the store is hidden so the
      next visit starts at the surprise chest hero, not mid-list. */
  resetScroll(): void {
    const content = this.element.querySelector<HTMLElement>('.store-content');
    if (content) content.scrollTop = 0;
  }

  /** Update wallet display (e.g. after a purchase). */
  updateWallet(wallet: Partial<StoreWallet>): void {
    Object.assign(this.data.wallet, wallet);
    const stacks = this.element.querySelector<HTMLElement>('.store-pill.stacks .store-pill-val');
    const gold = this.element.querySelector<HTMLElement>('.store-pill.gold .store-pill-val');
    const tokens = this.element.querySelector<HTMLElement>('.store-pill.tokens .store-pill-val');
    if (stacks) stacks.textContent = this.data.wallet.stacks;
    if (gold) gold.textContent = this.data.wallet.gold;
    if (tokens) tokens.textContent = this.data.wallet.diceTokens;
  }

  /** Parsed grant amounts for a bundle, from its displayed values. The dev stub
   *  uses these so the reward reflects the tapped item; the real grant amounts
   *  come from the server (peak-scaled). */
  getBundleAmounts(productId: string): { stacks: number; gold: number; diceTokens: number } | null {
    const b = this.data.bundles.find((x) => x.productId === productId);
    if (!b) return null;
    return {
      stacks: StoreScreen.parseAmount(b.stacks),
      gold: StoreScreen.parseAmount(b.gold),
      diceTokens: StoreScreen.parseAmount(b.diceTokens),
    };
  }

  private static parseAmount(s: string): number {
    const t = s.trim().replace(/,/g, '');
    const m = t.match(/^([\d.]+)\s*([kmbt]?)$/i);
    if (!m) return Math.round(Number(t)) || 0;
    const mult: Record<string, number> = { '': 1, k: 1e3, m: 1e6, b: 1e9, t: 1e12 };
    return Math.round(parseFloat(m[1]) * (mult[m[2].toLowerCase()] ?? 1));
  }

  /** Screen rect of a bundle list item — the flyout spawn origin. */
  getBundleRect(productId: string): DOMRect | null {
    const el = this.element.querySelector<HTMLElement>(`.store-bundle[data-product="${productId}"]`);
    return el ? el.getBoundingClientRect() : null;
  }

  /** Screen rect of a wallet pill value — the flyout target. */
  getWalletPillRect(kind: 'stacks' | 'gold' | 'tokens'): DOMRect | null {
    const el = this.element.querySelector<HTMLElement>(`.store-pill.${kind} .store-pill-val`);
    return el ? el.getBoundingClientRect() : null;
  }

  /** Count a wallet pill from→to with a landing pop. Driven by the cash-flyout's
   *  onLastNoteFlightStart so the number ticks up as the notes land on it. */
  countPillUp(kind: 'stacks' | 'gold' | 'tokens', from: number, to: number, ms = 900): void {
    StoreScreen.injectPillPopStyle();
    const el = this.element.querySelector<HTMLElement>(`.store-pill.${kind} .store-pill-val`);
    if (!el) return;
    const fmt = kind === 'stacks'
      ? (v: number) => StoreScreen.fmtStacks(v)
      : kind === 'gold'
        ? (v: number) => Math.round(v).toLocaleString('en-US')
        : (v: number) => String(Math.round(v));
    StoreScreen.countUp(el, from, to, ms, fmt);
    window.setTimeout(() => {
      el.classList.remove('store-pill-pop');
      void el.offsetWidth; // reflow to restart the animation
      el.classList.add('store-pill-pop');
    }, Math.max(0, ms - 150));
  }

  private static countUp(el: HTMLElement, from: number, to: number, ms: number, fmt: (v: number) => string): void {
    const startT = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - startT) / ms);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = fmt(from + (to - from) * eased);
      if (t < 1) requestAnimationFrame(step);
      else el.textContent = fmt(to);
    };
    requestAnimationFrame(step);
  }

  private static fmtStacks(n: number): string {
    const v = Math.round(n);
    if (v >= 1e12) return `${(v / 1e12).toFixed(v % 1e12 ? 1 : 0)}T`;
    if (v >= 1e9) return `${(v / 1e9).toFixed(v % 1e9 ? 1 : 0)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(v % 1e6 ? 1 : 0)}M`;
    if (v >= 1e3) return `${(v / 1e3).toFixed(v % 1e3 ? 1 : 0)}K`;
    return `${v}`;
  }

  private static injectPillPopStyle(): void {
    if (document.getElementById('sdl-store-pill-pop-style')) return;
    const style = document.createElement('style');
    style.id = 'sdl-store-pill-pop-style';
    style.textContent = `
      .store-pill-pop { animation: sdl-store-pill-pop 0.34s cubic-bezier(0.34, 1.56, 0.64, 1); }
      @keyframes sdl-store-pill-pop {
        0% { transform: scale(1); } 45% { transform: scale(1.18); } 100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
  }

  destroy(): void {
    this.element.remove();
  }
}
